import { useState, useEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import type { Asset } from "@/lib/types"
import type { ArtJobResponse } from "@/lib/api-types"
import { AssetSidebar } from "@/components/asset-sidebar"
import { TemplateSelector } from "@/components/template-selector"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useToast } from "@/hooks/use-toast"
import { useArtJob } from "@/hooks/useArtJob"
import { useConjunto } from "@/hooks/useConjunto"
import { useTemplates } from "@/hooks/useTemplates"
import { Loader2, Download, Save, LogOut, Plus } from "lucide-react"
import { apiClient } from "@/lib/api-client"
import { downloadFile } from "@/lib/utils"

export default function EditorPage() {
  const { artId } = useParams<{ artId: string }>()
  const navigate = useNavigate()
  const [art, setArt] = useState<ArtJobResponse | null>(null)
  const [assets, setAssets] = useState<Asset[]>([])
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()
  const { fetchArtJob } = useArtJob()
  const { createConjunto, exporting } = useConjunto()
  const { createTemplate } = useTemplates()

  // Drag state
  const [draggedAssetId, setDraggedAssetId] = useState<string | null>(null)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const canvasRef = useRef<HTMLDivElement>(null)
  const hasLoadedRef = useRef(false)

  // Zoom state
  const [zoom, setZoom] = useState(0.5)
  const MIN_ZOOM = 0.25
  const MAX_ZOOM = 3

  // Asset Sidebar state
  const [assetSidebarOpen, setAssetSidebarOpen] = useState(false)

  // ✅ Touch state para mobile
  const [touchMode, setTouchMode] = useState<'none' | 'move' | 'resize' | 'pan'>('none')
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 })
  const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 })

  // Save template dialog
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false)
  const [templateName, setTemplateName] = useState("")
  const [templateWidth, setTemplateWidth] = useState(0)
  const [templateHeight, setTemplateHeight] = useState(0)
  const [generatedS3Url, setGeneratedS3Url] = useState<string | null>(null)
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)

  const handleLogout = async () => {
    try {
      await apiClient.logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
      navigate('/login')
    }
  }

  const handleGeneratePNG = async () => {
    try {
      // 1. Criar NOVO template fresh com as dimensões corretas (nunca reutilizar)
      const tempTemplate = await createTemplate(
        `Temp PNG ${Date.now()}`,
        art?.width || 1024,
        art?.height || 1024
      )

      if (!tempTemplate) {
        toast({
          title: "Erro",
          description: "Erro ao criar template",
          variant: "destructive",
        })
        return
      }

      // 2. Adicionar cada asset como uma camada ao novo template
      for (const asset of assets) {
        if (!asset.backendAssetId) {
          console.warn(`Asset ${asset.name} não tem backendAssetId, pulando...`)
          continue
        }

        try {
          await apiClient.addLayer(tempTemplate.id, {
            kind: "asset",
            z_index: asset.z,
            x: Math.round(asset.x),
            y: Math.round(asset.y),
            w: Math.round(asset.w),
            h: Math.round(asset.h),
            rotation: asset.rotation || 0,
            asset_id: asset.backendAssetId,
          })
        } catch (err: any) {
          console.error(`Erro ao adicionar asset ${asset.name}:`, err)
        }
      }

      // 3. Criar conjunto com o template fresh
      const conjunto = await createConjunto({
        name: `PNG ${new Date().toLocaleString()}`,
        template_id: tempTemplate.id,
        art_id: artId!,
        art_x: 0,
        art_y: 0,
        art_w: art?.width || 1024,
        art_h: art?.height || 1024,
        art_rotation: 0,
        text_overrides: {},
      })

      if (!conjunto) {
        return
      }

      // 4. Usar s3_url do conjunto para download direto
      if (conjunto.s3_url) {
        setGeneratedS3Url(conjunto.s3_url!)
        
        try {
          // Download using the robust downloadFile function
          await downloadFile(
            conjunto.s3_url!,
            `composicao_${new Date().toLocaleString().replace(/[:\\/]/g, '-')}.png`
          )
          
          toast({
            title: "PNG Gerado!",
            description: "Download iniciado. Arquivo salvo em seu computador.",
          })
        } catch (downloadError) {
          console.error("Download error:", downloadError)
          toast({
            title: "Aviso",
            description: "PNG gerado com sucesso, mas houve um erro no download. Você pode acessá-lo via link.",
            variant: "default",
          })
        }
      }
    } catch (error) {
      console.error("Generate PNG error:", error)
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Erro ao gerar PNG",
        variant: "destructive",
      })
    }
  }

  const handleSaveTemplate = async () => {
    try {
      if (assets.length === 0) {
        toast({
          title: "Aviso",
          description: "Adicione pelo menos um asset antes de salvar o template",
          variant: "destructive",
        })
        return
      }

      // Se não há template selecionado, abrir dialog para criar novo
      if (!selectedTemplateId) {
        setSaveTemplateOpen(true)
        setTemplateName(`Meu Template ${new Date().toLocaleDateString()}`)
        setTemplateWidth(art?.width || 1024)
        setTemplateHeight(art?.height || 1024)
        return
      }

      // Se há template selecionado, salvar diretamente sem abrir dialog
      setIsSavingTemplate(true)
      
      // Limpar assets anteriores do template para evitar duplicação
      try {
        await apiClient.deleteAllLayers(selectedTemplateId)
      } catch (err: any) {
        console.warn("Erro ao limpar layers antigos:", err)
      }

      // Adicionar os assets atuais como layers ao template selecionado
      for (const asset of assets) {
        if (!asset.backendAssetId) {
          console.warn(`Asset ${asset.name} não tem backendAssetId, pulando...`)
          continue
        }

        try {
          await apiClient.addLayer(selectedTemplateId, {
            kind: "asset",
            z_index: asset.z,
            x: Math.round(asset.x),
            y: Math.round(asset.y),
            w: Math.round(asset.w),
            h: Math.round(asset.h),
            rotation: asset.rotation || 0,
            asset_id: asset.backendAssetId,
            skip_normalization: true,
          })
        } catch (err: any) {
          console.error(`Erro ao adicionar asset ${asset.name}:`, err)
        }
      }

      toast({
        title: "Template Salvo!",
        description: `Estado do template foi salvo com sucesso.`,
      })

      setIsSavingTemplate(false)
    } catch (error) {
      console.error("Save template error:", error)
      toast({
        title: "Erro",
        description: "Erro ao salvar template",
        variant: "destructive",
      })
      setIsSavingTemplate(false)
    }
  }

  const handleConfirmSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast({
        title: "Erro",
        description: "Nome do template é obrigatório",
        variant: "destructive",
      })
      return
    }

    setIsSavingTemplate(true)
    try {
      // 1. Criar template com o nome fornecido e dimensões personalizadas
      const savedTemplate = await createTemplate(
        templateName,
        templateWidth || art?.width || 1024,
        templateHeight || art?.height || 1024
      )

      if (!savedTemplate) {
        toast({
          title: "Erro",
          description: "Erro ao criar template",
          variant: "destructive",
        })
        return
      }

      // 2. Adicionar cada asset como layer ao template
      for (const asset of assets) {
        if (!asset.backendAssetId) {
          console.warn(`Asset ${asset.name} não tem backendAssetId, pulando...`)
          continue
        }

        try {
          await apiClient.addLayer(savedTemplate.id, {
            kind: "asset",
            z_index: asset.z,
            x: Math.round(asset.x),
            y: Math.round(asset.y),
            w: Math.round(asset.w),
            h: Math.round(asset.h),
            rotation: asset.rotation || 0,
            asset_id: asset.backendAssetId,
          })
        } catch (err: any) {
          console.error(`Erro ao adicionar asset ${asset.name}:`, err)
        }
      }

      toast({
        title: "Template Salvo!",
        description: `Template "${templateName}" foi salvo com sucesso e pode ser reutilizado.`,
      })

      setSaveTemplateOpen(false)
      setTemplateName("")
      setTemplateWidth(0)
      setTemplateHeight(0)
    } catch (error) {
      console.error("Confirm save template error:", error)
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Erro ao salvar template",
        variant: "destructive",
      })
    } finally {
      setIsSavingTemplate(false)
    }
  }

  // Load art
  useEffect(() => {
    const loadArt = async () => {
      // Se já carregou, não faz nada
      if (hasLoadedRef.current) {
        console.log('[DEBUG] Arte já foi carregada, skipping...')
        return
      }
      
      if (!artId) {
        setLoading(false)
        return
      }
      
      try {
        setLoading(true)
        hasLoadedRef.current = true
        
        // Aguardar um pouco para garantir que a API está pronta
        await new Promise(resolve => setTimeout(resolve, 300))
        
        console.log('[DEBUG] Carregando arte com ID:', artId)
        const response = await apiClient.getArtJob(artId)
        
        if (response && response.data) {
          console.log('[DEBUG] Fetched art:', response.data)
          console.log('[DEBUG] Art dimensions:', response.data.width, 'x', response.data.height)
          setArt(response.data)
        } else {
          console.error('[DEBUG] Falha ao carregar arte - resposta vazia')
        }
      } catch (error) {
        console.error('[DEBUG] Erro ao carregar arte:', error)
        hasLoadedRef.current = false // Reset em caso de erro
      } finally {
        setLoading(false)
      }
    }
    
    loadArt()
  }, [artId])

  // Handle drag and drop
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggedAssetId || !canvasRef.current) return
      
      if (!art || !art.width || !art.height) return
      
      const canvasRect = canvasRef.current.getBoundingClientRect()
      const scaleX = canvasRect.width / art.width
      const scaleY = canvasRect.height / art.height
      
      const visualX = e.clientX - canvasRect.left
      const visualY = e.clientY - canvasRect.top
      const logicalX = visualX / scaleX
      const logicalY = visualY / scaleY
      
      const newX = logicalX - dragOffset.x
      const newY = logicalY - dragOffset.y
      
      setAssets((assets) =>
        assets.map((a) =>
          a.id === draggedAssetId ? { ...a, x: newX, y: newY } : a
        )
      )
    }

    const handleMouseUp = () => {
      setDraggedAssetId(null)
    }

    if (draggedAssetId) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [draggedAssetId, dragOffset, art])

  if (loading) {
    return (
      <div className="fixed inset-0 w-screen h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-purple-300 text-sm">Carregando editor...</p>
        </div>
      </div>
    )
  }

  if (!art) {
    return (
      <div className="fixed inset-0 w-screen h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="text-center">
          <p className="text-purple-300 text-sm">Arte não encontrada</p>
        </div>
      </div>
    )
  }

  const handleUpdateAsset = (id: string, updates: Partial<Asset>) => {
    setAssets(assets.map((a) => (a.id === id ? { ...a, ...updates } : a)))
  }

  const handleDeleteAsset = (id: string) => {
    setAssets(assets.filter((a) => a.id !== id))
    if (selectedAssetId === id) {
      setSelectedAssetId(null)
    }
  }

  const handleApplyTemplate = (templateAssets: Omit<Asset, "id">[], templateId?: string) => {
    const newAssets = templateAssets.map((asset, index) => ({
      ...asset,
      id: `template-asset-${Date.now()}-${index}`,
    }))
    setAssets(newAssets)
    setSelectedAssetId(null)
    if (templateId) {
      setSelectedTemplateId(templateId)
    }
  }

  const handleAddAsset = (newAsset: Asset) => {
    let asset = newAsset
    if (newAsset.x === 50 && newAsset.y === 50 && art?.width && art?.height) {
      const canvasWidth = art.width
      const canvasHeight = art.height
      const centerX = (canvasWidth - newAsset.w) / 2
      const centerY = (canvasHeight - newAsset.h) / 2
      
      asset = {
        ...newAsset,
        x: Math.max(0, centerX),
        y: Math.max(0, centerY),
      }
    }
    
    if (!selectedTemplateId && art?.width && art?.height) {
      createTemplate(
        `Canvas ${art.width}x${art.height} - ${new Date().toLocaleString()}`,
        art.width!,
        art.height!
      ).then((newTemplate) => {
        if (newTemplate) {
          setSelectedTemplateId(newTemplate.id)
          toast({
            title: "Template criado",
            description: `Canvas ${art.width}x${art.height}px pronto para adicionar assets`,
          })
        }
      }).catch((error) => {
        console.error("Erro ao criar template:", error)
      })
    }
    
    setAssets((prev) => [...prev, asset])
    setSelectedAssetId(asset.id)
    toast({
      title: "Asset adicionado",
      description: `${newAsset.name} foi adicionado ao canvas`,
    })
  }

  const handleMouseDown = (e: React.MouseEvent, assetId: string) => {
    if ((e.target as HTMLElement).classList.contains('resize-handle')) {
      return
    }
    
    e.preventDefault()
    setSelectedAssetId(assetId)
    setDraggedAssetId(assetId)
    
    const asset = assets.find(a => a.id === assetId)
    if (asset && canvasRef.current && art) {
      const canvasRect = canvasRef.current.getBoundingClientRect()
      const scaleX = canvasRect.width / art.width
      const scaleY = canvasRect.height / art.height
      
      const visualX = e.clientX - canvasRect.left
      const visualY = e.clientY - canvasRect.top
      const logicalX = visualX / scaleX
      const logicalY = visualY / scaleY
      
      setDragOffset({
        x: logicalX - asset.x,
        y: logicalY - asset.y,
      })
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = "copy"
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    try {
      const data = e.dataTransfer.getData("application/json")
      if (data) {
        const droppedAsset = JSON.parse(data)
        console.log("Dropped asset:", droppedAsset)
        
        if (!droppedAsset.imageUrl) {
          console.warn("Asset sem imageUrl:", droppedAsset)
          return
        }
        
        if (!canvasRef.current || !art) return
        
        const canvasRect = canvasRef.current.getBoundingClientRect()
        
        let visualX = e.clientX - canvasRect.left
        let visualY = e.clientY - canvasRect.top
        
        const scaleX = zoom
        const scaleY = zoom
        
        let x = visualX / scaleX
        let y = visualY / scaleY
        
        const assetWidth = droppedAsset.width || 150
        const assetHeight = droppedAsset.height || 150
        
        x = x - assetWidth / 2
        y = y - assetHeight / 2
        
        x = Math.max(0, Math.min(x, art.width - assetWidth))
        y = Math.max(0, Math.min(y, art.height - assetHeight))

        const newAsset: Asset = {
          id: `asset-${Date.now()}`,
          name: droppedAsset.name,
          imageUrl: droppedAsset.imageUrl,
          x: x,
          y: y,
          z: Math.max(...assets.map(a => a.z), 0) + 1,
          w: assetWidth,
          h: assetHeight,
          rotation: 0,
          backendAssetId: droppedAsset.id,
        }

        handleAddAsset(newAsset)
      }
    } catch (error) {
      console.error("Drop error:", error)
    }
  }

  const handleCanvasClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).classList.contains('asset-overlay')) {
      setSelectedAssetId(null)
    }
  }

  // ✅ Handlers de touch para mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && selectedAssetId) {
      // Um dedo = mover asset
      setTouchMode('move')
      setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY })
    } else if (e.touches.length === 2) {
      // Dois dedos = resize ou pan
      const touch1 = e.touches[0]
      const touch2 = e.touches[1]
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      )
      setTouchMode('resize')
      setTouchStart({ x: distance, y: 0 })
    } else if (e.touches.length === 1 && !selectedAssetId) {
      // Um dedo SEM asset selecionado = pan na tela
      setTouchMode('pan')
      setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY })
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && touchMode === 'move' && selectedAssetId && canvasRef.current && art) {
      const asset = assets.find(a => a.id === selectedAssetId)
      if (!asset) return

      const canvasRect = canvasRef.current.getBoundingClientRect()
      const scaleX = canvasRect.width / art.width
      const scaleY = canvasRect.height / art.height

      const dx = (e.touches[0].clientX - touchStart.x) / scaleX
      const dy = (e.touches[0].clientY - touchStart.y) / scaleY

      handleUpdateAsset(selectedAssetId, {
        x: Math.max(0, Math.min(asset.x + dx, art.width - asset.w)),
        y: Math.max(0, Math.min(asset.y + dy, art.height - asset.h)),
      })

      setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY })
    } else if (e.touches.length === 2 && touchMode === 'resize' && selectedAssetId) {
      // Resize com 2 dedos
      const touch1 = e.touches[0]
      const touch2 = e.touches[1]
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      )

      const scale = distance / touchStart.x
      const asset = assets.find(a => a.id === selectedAssetId)
      if (asset) {
        const newW = Math.max(30, asset.w * scale)
        const newH = Math.max(30, asset.h * scale)
        handleUpdateAsset(selectedAssetId, { w: newW, h: newH })
      }

      setTouchStart({ x: distance, y: 0 })
    } else if (e.touches.length === 1 && touchMode === 'pan') {
      // ✅ Pan na tela - arrasta o canvas
      const dx = e.touches[0].clientX - touchStart.x
      const dy = e.touches[0].clientY - touchStart.y

      setCanvasPan({
        x: canvasPan.x + dx,
        y: canvasPan.y + dy,
      })

      setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY })
    }
  }

  const handleTouchEnd = () => {
    setTouchMode('none')
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-purple-100 via-purple-50 to-purple-100">
      {/* Header */}
      <header className="border-b bg-gradient-to-r from-purple-400 to-purple-300 backdrop-blur-sm border-purple-500/50">
        <div className="container flex items-center justify-between h-16 px-4">
          <div className="flex items-center gap-2 md:gap-4">
            <h1 className="hidden md:block text-xl font-semibold text-purple-900">Editor de Arte</h1>
            <TemplateSelector onApplyTemplate={handleApplyTemplate} artWidth={art?.width} artHeight={art?.height} />
          </div>
          <div className="flex gap-2 md:gap-3 items-center">
            {/* Zoom Controls - Apenas em Desktop */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-purple-300/80 border-purple-500/60 rounded-lg border">
              <Button
                onClick={() => setZoom(Math.max(MIN_ZOOM, zoom - 0.25))}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-lg font-bold"
              >
                −
              </Button>
              <span className="text-sm font-medium w-14 text-center text-purple-900">{Math.round(zoom * 100)}%</span>
              <Button
                onClick={() => setZoom(Math.min(MAX_ZOOM, zoom + 0.25))}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-lg font-bold"
              >
                +
              </Button>
            </div>
            
            {/* ✅ Zoom Controls - Mobile (minúsculo) */}
            <div className="md:hidden flex items-center gap-0.5 px-1.5 py-0.5 bg-purple-300/80 rounded border border-purple-500/60">
              <Button
                onClick={() => setZoom(Math.max(MIN_ZOOM, zoom - 0.1))}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-xs font-bold"
              >
                −
              </Button>
              <span className="text-[10px] font-medium w-8 text-center text-purple-900">{Math.round(zoom * 100)}%</span>
              <Button
                onClick={() => setZoom(Math.min(MAX_ZOOM, zoom + 0.1))}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-xs font-bold"
              >
                +
              </Button>
            </div>
            
            {/* Mobile: Apenas ícone, Desktop: com texto */}
            <Button
              onClick={handleSaveTemplate}
              disabled={exporting || loading || assets.length === 0}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 md:px-3"
              variant="default"
              size="sm"
            >
              <Save className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Salvar Template</span>
            </Button>
            
            {/* Gerar PNG - Mobile e Desktop */}
            <Button
              onClick={handleGeneratePNG}
              disabled={exporting || loading || !art || !art.s3_url}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              variant="default"
              size="sm"
            >
              <Download className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">{exporting ? 'Gerando...' : 'Gerar PNG'}</span>
              {exporting && <span className="md:hidden"><Loader2 className="h-4 w-4 animate-spin" /></span>}
            </Button>
            
            {/* Logout - Mobile e Desktop */}
            <Button
              onClick={handleLogout}
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
              variant="default"
              size="sm"
            >
              <LogOut className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>
      
      {/* Remover botão logout fixo - agora está no header */}

      <div className="flex-1 flex overflow-hidden relative">
        <div className="flex-1 flex items-start justify-center pt-12 px-8 overflow-auto md:overflow-auto overflow-hidden md:overflow-y-auto bg-gradient-to-br from-purple-100 to-purple-50 pb-0 mb-0" onDragOver={handleDragOver} onDrop={handleDrop} onClick={handleCanvasClick} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
          {art ? (
            <div 
              ref={canvasRef}
              className="relative bg-white rounded-lg shadow-2xl overflow-hidden border-2 border-purple-300 flex-shrink-0"
              style={{
                width: `${(art.width || 1024) * zoom}px`,
                height: `${(art.height || 1024) * zoom}px`,
                marginRight: '40px',
                marginBottom: '40px',
                marginTop: '20px',
                boxShadow: "0 0 40px rgba(139, 92, 246, 0.5)",
                transform: `translate(${canvasPan.x}px, ${canvasPan.y}px)`,
                transition: touchMode === 'none' ? 'transform 0.1s ease-out' : 'none'
              }}
            >
              <img
                src={art.s3_url || "/placeholder.svg"}
                alt="Generated art"
                className="w-full h-full object-cover"
              />
              {assets.length > 0 && (
                <div className="asset-overlay absolute inset-0">
                  {assets.map((asset) => (
                    <div
                      key={asset.id}
                      className={`absolute rounded-md ${
                        selectedAssetId === asset.id
                          ? "border-2 border-blue-500 bg-blue-500/20"
                          : ""
                      }`}
                      style={{
                        left: `${asset.x * zoom}px`,
                        top: `${asset.y * zoom}px`,
                        width: `${asset.w * zoom}px`,
                        height: `${asset.h * zoom}px`,
                        transform: `rotate(${asset.rotation || 0}deg)`,
                        zIndex: asset.z,
                        cursor: "move",
                      }}
                      onMouseDown={(e) => handleMouseDown(e, asset.id)}
                    >
                      {asset.imageUrl ? (
                        <img
                          src={asset.imageUrl}
                          alt={asset.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600">
                          {asset.name} (Imagem não disponível)
                        </div>
                      )}

                      {selectedAssetId === asset.id && (
                        <>
                          <div
                            className="resize-handle absolute -top-2 -left-2 w-12 h-12 md:w-6 md:h-6 bg-blue-500 rounded-full cursor-nwse-resize border border-white shadow-md z-50 transition-all hover:w-10 hover:h-10 md:hover:w-7 md:hover:h-7"
                            onMouseDown={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              const startX = e.clientX
                              const startY = e.clientY
                              const startW = asset.w
                              const startH = asset.h
                              const startX_asset = asset.x
                              const startY_asset = asset.y

                              const handleMove = (moveEvent: MouseEvent) => {
                                const deltaX = moveEvent.clientX - startX
                                const deltaY = moveEvent.clientY - startY
                                handleUpdateAsset(asset.id, {
                                  w: Math.max(20, startW - deltaX),
                                  h: Math.max(20, startH - deltaY),
                                  x: startX_asset + deltaX,
                                  y: startY_asset + deltaY,
                                })
                              }

                              const handleUp = () => {
                                window.removeEventListener('mousemove', handleMove)
                                window.removeEventListener('mouseup', handleUp)
                              }

                              window.addEventListener('mousemove', handleMove)
                              window.addEventListener('mouseup', handleUp)
                            }}
                          />

                          <div
                            className="resize-handle absolute -top-2 -right-2 w-12 h-12 md:w-6 md:h-6 bg-blue-500 rounded-full cursor-nesw-resize border border-white shadow-md z-50 transition-all hover:w-10 hover:h-10 md:hover:w-7 md:hover:h-7"
                            onMouseDown={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              const startX = e.clientX
                              const startY = e.clientY
                              const startW = asset.w
                              const startH = asset.h
                              const startY_asset = asset.y

                              const handleMove = (moveEvent: MouseEvent) => {
                                const deltaX = moveEvent.clientX - startX
                                const deltaY = moveEvent.clientY - startY
                                handleUpdateAsset(asset.id, {
                                  w: Math.max(20, startW + deltaX),
                                  h: Math.max(20, startH - deltaY),
                                  y: startY_asset + deltaY,
                                })
                              }

                              const handleUp = () => {
                                window.removeEventListener('mousemove', handleMove)
                                window.removeEventListener('mouseup', handleUp)
                              }

                              window.addEventListener('mousemove', handleMove)
                              window.addEventListener('mouseup', handleUp)
                            }}
                          />

                          <div
                            className="resize-handle absolute -bottom-2 -left-2 w-12 h-12 md:w-6 md:h-6 bg-blue-500 rounded-full cursor-nesw-resize border border-white shadow-md z-50 transition-all hover:w-10 hover:h-10 md:hover:w-7 md:hover:h-7"
                            onMouseDown={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              const startX = e.clientX
                              const startY = e.clientY
                              const startW = asset.w
                              const startH = asset.h
                              const startX_asset = asset.x

                              const handleMove = (moveEvent: MouseEvent) => {
                                const deltaX = moveEvent.clientX - startX
                                const deltaY = moveEvent.clientY - startY
                                handleUpdateAsset(asset.id, {
                                  w: Math.max(20, startW - deltaX),
                                  h: Math.max(20, startH + deltaY),
                                  x: startX_asset + deltaX,
                                })
                              }

                              const handleUp = () => {
                                window.removeEventListener('mousemove', handleMove)
                                window.removeEventListener('mouseup', handleUp)
                              }

                              window.addEventListener('mousemove', handleMove)
                              window.addEventListener('mouseup', handleUp)
                            }}
                            // ✅ Suporte a touch para mobile
                            onTouchStart={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              if (e.touches.length !== 1) return
                              
                              const startX = e.touches[0].clientX
                              const startY = e.touches[0].clientY
                              const startW = asset.w
                              const startH = asset.h
                              const startX_asset = asset.x

                              const handleTouchMove = (moveEvent: TouchEvent) => {
                                if (moveEvent.touches.length !== 1) return
                                const deltaX = moveEvent.touches[0].clientX - startX
                                const deltaY = moveEvent.touches[0].clientY - startY
                                handleUpdateAsset(asset.id, {
                                  w: Math.max(20, startW - deltaX),
                                  h: Math.max(20, startH + deltaY),
                                  x: startX_asset + deltaX,
                                })
                              }

                              const handleTouchEnd = () => {
                                window.removeEventListener('touchmove', handleTouchMove)
                                window.removeEventListener('touchend', handleTouchEnd)
                              }

                              window.addEventListener('touchmove', handleTouchMove)
                              window.addEventListener('touchend', handleTouchEnd)
                            }}
                          />

                          <div
                            className="resize-handle absolute -bottom-2 -right-2 w-12 h-12 md:w-6 md:h-6 bg-blue-500 rounded-full cursor-se-resize border border-white shadow-md z-50 transition-all hover:w-10 hover:h-10 md:hover:w-7 md:hover:h-7"
                            onMouseDown={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              const startX = e.clientX
                              const startY = e.clientY
                              const startW = asset.w
                              const startH = asset.h

                              const handleMove = (moveEvent: MouseEvent) => {
                                const deltaX = moveEvent.clientX - startX
                                const deltaY = moveEvent.clientY - startY
                                handleUpdateAsset(asset.id, {
                                  w: Math.max(20, startW + deltaX),
                                  h: Math.max(20, startH + deltaY),
                                })
                              }

                              const handleUp = () => {
                                window.removeEventListener('mousemove', handleMove)
                                window.removeEventListener('mouseup', handleUp)
                              }

                              window.addEventListener('mousemove', handleMove)
                              window.addEventListener('mouseup', handleUp)
                            }}
                            // ✅ Suporte a touch para mobile
                            onTouchStart={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              if (e.touches.length !== 1) return
                              
                              const startX = e.touches[0].clientX
                              const startY = e.touches[0].clientY
                              const startW = asset.w
                              const startH = asset.h

                              const handleTouchMove = (moveEvent: TouchEvent) => {
                                if (moveEvent.touches.length !== 1) return
                                const deltaX = moveEvent.touches[0].clientX - startX
                                const deltaY = moveEvent.touches[0].clientY - startY
                                handleUpdateAsset(asset.id, {
                                  w: Math.max(20, startW + deltaX),
                                  h: Math.max(20, startH + deltaY),
                                })
                              }

                              const handleTouchEnd = () => {
                                window.removeEventListener('touchmove', handleTouchMove)
                                window.removeEventListener('touchend', handleTouchEnd)
                              }

                              window.addEventListener('touchmove', handleTouchMove)
                              window.addEventListener('touchend', handleTouchEnd)
                            }}
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Asset Sidebar - Colapsável em Mobile */}
        <div className={`fixed md:relative sm:hidden lg:block bottom-0 left-0 right-0 md:bottom-auto md:flex flex-col w-full md:w-80 bg-transparent md:bg-white border-t md:border-t-0 md:border-l border-purple-200 shadow-lg md:shadow-none transition-all duration-300 z-40 ${
          assetSidebarOpen ? 'h-[70vh]' : 'h-12 md:h-auto'
        }`}>
          {/* Toggle Button - Visível apenas em Mobile */}
          <div className="md:hidden flex items-center justify-between px-4 py-2 bg-gradient-to-r from-purple-400 to-purple-300 cursor-pointer mt-4" onClick={() => setAssetSidebarOpen(!assetSidebarOpen)}>
            <span className="font-semibold text-purple-900">📦 Biblioteca de Assets</span>
          </div>
          
          {/* Sidebar Content */}
          <div className="hidden md:block flex-1 overflow-hidden">
            <AssetSidebar
              assets={assets}
              selectedAssetId={selectedAssetId}
              onSelectAsset={setSelectedAssetId}
              onUpdateAsset={handleUpdateAsset}
              onDeleteAsset={handleDeleteAsset}
              onAddAsset={handleAddAsset}
            />
          </div>
          
          {/* Mobile Sidebar Content */}
          {assetSidebarOpen && (
            <div className="md:hidden flex-1 overflow-hidden bg-white rounded-t-lg">
              <AssetSidebar
                assets={assets}
                selectedAssetId={selectedAssetId}
                onSelectAsset={setSelectedAssetId}
                onUpdateAsset={handleUpdateAsset}
                onDeleteAsset={handleDeleteAsset}
                onAddAsset={handleAddAsset}
              />
            </div>
          )}
        </div>
        
        <Button
          onClick={() => navigate('/')}
          className="hidden md:flex fixed top-1/2 left-4 transform -translate-y-1/2 w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 shadow-lg hover:shadow-xl transition-all"
          size="icon"
        >
          <Plus className="h-6 w-6 text-white" />
        </Button>
      </div>

      <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Salvar Template</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="template-name" className="text-right">
                Nome do Template:
              </Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="template-width" className="text-right">
                Largura (px):
              </Label>
              <Input
                id="template-width"
                type="number"
                value={templateWidth}
                onChange={(e) => setTemplateWidth(Number(e.target.value))}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="template-height" className="text-right">
                Altura (px):
              </Label>
              <Input
                id="template-height"
                type="number"
                value={templateHeight}
                onChange={(e) => setTemplateHeight(Number(e.target.value))}
                className="col-span-3"
              />
            </div>
            <Button onClick={handleConfirmSaveTemplate} disabled={isSavingTemplate}>
              {isSavingTemplate ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar Template"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}


